/** Floors and doors of a building (`.etage`, `.porte`), top floor first. */
import { Badge } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { phantomConfidenceLabel } from '../utils/phantom';

import type { BadgeTone } from '../../../components/ui';
import type { Building } from '../../../api/types';
import type { BuildingDoor } from '../types';

export interface FloorListProps {
  building: Building;
  /** Called only for the door currently assigned to the caller (`mine`). */
  onSelectMine: () => void;
}

interface DoorStatus {
  label: string;
  tone: BadgeTone;
  rowClass: string;
  clickable: boolean;
}

/**
 * Resolve a door's written status: exactly the four states
 * `docs/API.md` §5 gives the building view — recorded, mine, held by
 * someone else, or worth a second look.
 *
 * @param door - The door to describe.
 * @returns Its label, badge tone, row style and whether it is tappable.
 */
function doorStatus(door: BuildingDoor): DoorStatus {
  if (door.state === 'recorded') {
    return {
      label: t('session.building.status.done'),
      tone: 'green',
      rowClass: 'porte--faite',
      clickable: false,
    };
  }
  if (door.mine) {
    return {
      label: t('session.building.status.target'),
      tone: 'red',
      rowClass: 'porte--mine',
      clickable: true,
    };
  }
  if (door.held_by) {
    return {
      label: t('session.building.status.held', { name: door.held_by }),
      tone: 'neutral',
      rowClass: 'porte--autre',
      clickable: false,
    };
  }
  const confidenceLabel = door.kind !== 'real' ? phantomConfidenceLabel(door.confidence) : null;
  return {
    label: confidenceLabel
      ? t('session.building.status.toVerifyPhantom', { confidence: confidenceLabel })
      : t('session.building.status.toVerify'),
    tone: 'yellow',
    rowClass: '',
    clickable: false,
  };
}

/** What the "qui" column says: electors and non-voters, or "no known elector". */
function occupancyLabel(door: BuildingDoor): string {
  if (door.electors === 0) {
    return t('session.building.noElectors');
  }
  return `${t('session.building.electors', { count: door.electors })} · ${t('session.building.nonVoters', { count: door.non_voters })}`;
}

/**
 * Render one door row.
 *
 * @param props - The door and the tap handler for the caller's own door.
 * @returns The row element.
 */
function DoorRow({ door, onSelectMine }: { door: BuildingDoor; onSelectMine: () => void }) {
  const status = doorStatus(door);
  const classes = ['porte', status.rowClass].filter(Boolean).join(' ');

  if (status.clickable) {
    return (
      <button type="button" className={classes} onClick={onSelectMine}>
        <span className="apt">{door.label}</span>
        <span className="qui">{occupancyLabel(door)}</span>
        <Badge tone={status.tone}>{status.label}</Badge>
      </button>
    );
  }

  return (
    <div className={classes}>
      <span className="apt">{door.label}</span>
      <span className="qui">{occupancyLabel(door)}</span>
      <Badge tone={status.tone}>{status.label}</Badge>
    </div>
  );
}

/**
 * Render every floor of a building, top floor first (as `docs/API.md` §5
 * orders them), each with its doors.
 *
 * @param props - The building view and the tap handler for the caller's door.
 * @returns The floor list element.
 */
export function FloorList({ building, onSelectMine }: FloorListProps) {
  return (
    <div className="bloc">
      {building.floors.map((floor) => (
        <div className="etage" key={floor.floor ?? 'unknown'}>
          <p className="kicker">
            {floor.floor === null
              ? t('session.building.floorUnknown')
              : t('session.building.floorKicker', { floor: floor.floor })}
          </p>
          {floor.doors.map((door) => (
            <DoorRow key={door.id} door={door} onSelectMine={onSelectMine} />
          ))}
        </div>
      ))}
    </div>
  );
}
